/** Default analytics warehouse for new CUBE_CONFIG `data` (stored alongside `sql`, measures, dimensions). */
export const DEFAULT_CUBE_DATA_SOURCE = 'starrocks';
/**
 * Datasets use legacy infrastructure when `data_source` is not the new warehouse.
 * New infrastructure (Starrocks) is opt-in via the create/clone/version toggles (SSP-3988).
 */
export const isLegacyInfrastructure = (dataSource) => dataSource !== DEFAULT_CUBE_DATA_SOURCE;
/** Default for new-infrastructure toggles on create, clone, and new-version flows (SSP-3988). */
export const DEFAULT_USE_NEW_INFRASTRUCTURE = false;
/** Sentinel for "no format" — persisted as an omitted `format`, never an empty string. */
export const NO_DATA_FORMAT = '';
/** Data type options for the Data settings type selector. Labels are translation keys. */
export const DATA_FIELD_TYPE_OPTIONS = [
    { labelKey: 'Number', value: 'number' },
    { labelKey: 'String', value: 'string' },
    { labelKey: 'Boolean', value: 'boolean' },
    { labelKey: 'Date/Time', value: 'time' },
];
const NONE_FORMAT_OPTION = { labelKey: 'None', value: NO_DATA_FORMAT };
/**
 * Numeric Cube formats: plain number (with explicit decimal places), percentage, and
 * SI-abbreviation. 'number' with no suffix (the default assigned to Count/Sum/Average/Min/Max
 * measures) renders whole numbers without decimals and any other value at 2 decimals —
 * these explicit-decimal options let a user override that default per field.
 *
 * `labelKey` uses the spreadsheet-style pattern convention (e.g. 'N.00', 'N.00%') instead of
 * spelled-out text ('Percentage (2 decimals)') so each option reads at a glance in the closed
 * select, which is rendered at a fixed, narrow column width in Data settings. `descriptionKey`
 * spells the pattern out in words as nucleus `Select`'s secondary option line, shown only in the
 * open dropdown list — so the pattern stays legible without needing to widen the field.
 */
const NUMERIC_FORMAT_OPTIONS = [
    { labelKey: 'N', value: 'number_0', descriptionKey: 'Whole number' },
    { labelKey: 'N.0', value: 'number_1', descriptionKey: 'Number, 1 decimal' },
    { labelKey: 'N.00', value: 'number_2', descriptionKey: 'Number, 2 decimals' },
    { labelKey: 'N.0%', value: 'percent_1', descriptionKey: 'Percentage, 1 decimal' },
    { labelKey: 'N.00%', value: 'percent_2', descriptionKey: 'Percentage, 2 decimals' },
    { labelKey: 'N.0K', value: 'abbr_1', descriptionKey: 'Abbreviated (K/M/B), 1 decimal' },
    { labelKey: 'N.00K', value: 'abbr_2', descriptionKey: 'Abbreviated (K/M/B), 2 decimals' },
];
/** Hyperlink format — valid only on string dimensions in Cube (never on measures). */
const LINK_FORMAT_OPTION = {
    labelKey: 'Link',
    value: 'link',
    descriptionKey: 'Render the value as a clickable link',
};
/**
 * Cube `format` options available for a field, by category + data type (SSP-3972). The first entry is
 * always "None". This intentionally mirrors the cube-dev validator (data-models/format-utils.js), where:
 *   - measure formats must be numeric (percent_N / abbr_N / d3) — `link` is rejected on measures;
 *   - dimension formats depend on type — `link` only for string, numeric for number, none for boolean/time.
 * Keeping this a strict subset of cube-dev's allow-list guarantees a saved config never fails Cube
 * schema validation (an invalid format would otherwise drop the whole dataset from the data model).
 */
export const getDataFormatOptions = (category, type) => {
    // Measures only support numeric Cube formats, and only when the data type is numeric.
    if (category === 'Measure') {
        return type === 'number' ? [NONE_FORMAT_OPTION, ...NUMERIC_FORMAT_OPTIONS] : [NONE_FORMAT_OPTION];
    }
    // Dimensions: string -> link, number -> numeric formats, boolean/time -> none.
    if (type === 'string') {
        return [NONE_FORMAT_OPTION, LINK_FORMAT_OPTION];
    }
    if (type === 'number') {
        return [NONE_FORMAT_OPTION, ...NUMERIC_FORMAT_OPTIONS];
    }
    return [NONE_FORMAT_OPTION];
};
/** The format selector is disabled when the only available option is "None". */
export const isDataFormatDisabled = (category, type) => getDataFormatOptions(category, type).length <= 1;
/** A format is valid when it is "None" or one of the field's allowed format values. */
export const isValidDataFormatForField = (category, type, format) => {
    if (!format) {
        return true;
    }
    return getDataFormatOptions(category, type).some((option) => option.value === format);
};
